// Appearance preference — light / dark / system. Persisted in
// localStorage and applied by setting data-vs-theme on <html>; the
// matching token overrides live in styles.css. 'system' clears the
// attribute so the prefers-color-scheme media query takes over.

export type ThemePref = 'light' | 'dark' | 'system';

const KEY = 'vs:theme-pref';

// Status-bar / browser-chrome tints. MUST stay in sync with the two
// static <meta name="theme-color"> media queries in index.html.
const THEME_COLOR = { light: '#F4F4F1', dark: '#08080B' } as const;
const ACTIVE_META_ID = 'vs-theme-color-active';

// Keep the iOS/Android status-bar tint matched to the *resolved* app
// theme. index.html ships two media-query theme-color metas keyed to the
// OS `prefers-color-scheme`, but the in-app override (data-vs-theme) can
// diverge from the OS — e.g. app forced light while the phone is in dark
// mode. Without this, the status bar follows the OS, not the app, leaving
// a mismatched band above the content. When pref is 'system' we drop the
// override and let the static media metas resolve per-OS again.
function syncThemeColor(pref: ThemePref): void {
  if (typeof document === 'undefined') return;
  let active = document.getElementById(ACTIVE_META_ID) as HTMLMetaElement | null;
  if (pref === 'system') {
    active?.remove();
    return;
  }
  if (!active) {
    active = document.createElement('meta');
    active.setAttribute('name', 'theme-color');
    active.id = ACTIVE_META_ID;
    // Insert FIRST in tree order: the UA picks the first theme-color whose
    // media matches, and this media-less meta always matches — so it wins
    // over the index.html media metas while present.
    const first = document.head.querySelector('meta[name="theme-color"]');
    document.head.insertBefore(active, first);
  }
  active.setAttribute('content', THEME_COLOR[pref]);
}

export function getThemePref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* private mode */
  }
  // §launch-defaults — Pomela defaults to DARK. An explicit choice (light /
  // dark / system) from Settings is saved above and always wins; only an unset
  // preference falls back here. Keep this in sync with the pre-paint inline
  // script in index.html so there is no wrong-theme flash.
  return 'dark';
}

export function applyTheme(pref: ThemePref): void {
  const root = document.documentElement;
  if (pref === 'system') root.removeAttribute('data-vs-theme');
  else root.setAttribute('data-vs-theme', pref);
  root.style.colorScheme = pref === 'system' ? 'light dark' : pref;
  syncThemeColor(pref);
}

export function setThemePref(pref: ThemePref): void {
  try {
    localStorage.setItem(KEY, pref);
  } catch {
    /* private mode — apply for the session anyway */
  }
  applyTheme(pref);
}
